import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Link2, FileText, ExternalLink, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CATEGORY_META = {
  leasing: { label: "Leasing / LLD / LOA", color: "bg-blue-100 text-blue-800 border-blue-200", icon: "🚗" },
  assurance: { label: "Assurance", color: "bg-amber-100 text-amber-800 border-amber-200", icon: "🛡️" },
  maintenance: { label: "Entretien / Maintenance", color: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: "🔧" },
  pret: { label: "Prêt / Crédit", color: "bg-purple-100 text-purple-800 border-purple-200", icon: "🏦" },
  credit: { label: "Prêt / Crédit", color: "bg-purple-100 text-purple-800 border-purple-200", icon: "🏦" },
  energie: { label: "Énergie / Carburant", color: "bg-orange-100 text-orange-800 border-orange-200", icon: "⛽" },
  autre: { label: "Autre", color: "bg-slate-100 text-slate-700 border-slate-200", icon: "📄" },
};

const fmtEur = (v) =>
  v != null && !isNaN(v)
    ? new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v)
    : "-";

const fmtDate = (d) => {
  if (!d) return "-";
  try {
    return new Date(d).toLocaleDateString("fr-FR");
  } catch {
    return d;
  }
};

/**
 * Affiche tous les engagements liés au véhicule avec KPI consolidés.
 * Cliquable → navigation vers la fiche du contrat.
 */
export default function LinkedEngagementsCard({ vehicleId, token }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/fleet/vehicles/${vehicleId}/linked-engagements`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      console.error("linked engagements err", e);
    } finally {
      setLoading(false);
    }
  }, [vehicleId, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <Card>
        <CardContent className="py-6 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  const engagements = data?.engagements || [];

  return (
    <Card data-testid="linked-engagements-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Link2 className="w-4 h-4 text-indigo-600" />
            Contrats liés (Dépenses avec Engagements)
            <Badge variant="outline" className="ml-1 text-xs">
              {engagements.length}
            </Badge>
          </CardTitle>
          {engagements.length > 0 && (
            <div className="flex items-baseline gap-4 text-sm">
              <div>
                <span className="text-slate-500">Total mensuel HT : </span>
                <span className="font-semibold text-slate-900">{fmtEur(data.total_monthly_ht)}</span>
              </div>
              <div>
                <span className="text-slate-500">Annuel HT : </span>
                <span className="font-semibold text-slate-900">{fmtEur(data.total_annual_ht)}</span>
              </div>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {engagements.length === 0 ? (
          <div className="text-center py-6 text-slate-500 text-sm">
            <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            Aucun contrat des Dépenses avec Engagements n&apos;est lié à ce véhicule.
            <div className="mt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/engagements/contrats")}
                data-testid="goto-engagements-btn"
              >
                Voir les contrats
              </Button>
            </div>
          </div>
        ) : (
          <div className="divide-y">
            {engagements.map((e) => {
              const meta = CATEGORY_META[e.categorie] || CATEGORY_META.autre;
              return (
                <div
                  key={e.id}
                  className="py-3 flex items-center justify-between gap-3 hover:bg-slate-50 transition rounded px-2 -mx-2 cursor-pointer"
                  onClick={() => navigate(`/engagements/contrats/${e.id}`)}
                  data-testid={`linked-engagement-row-${e.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-lg">{meta.icon}</span>
                      <span className="font-medium text-slate-900 truncate">{e.nom_contrat || "Contrat sans nom"}</span>
                      <Badge className={`${meta.color} text-[10px]`}>{meta.label}</Badge>
                      {e.statut && e.statut !== "actif" && (
                        <Badge variant="outline" className="text-[10px]">
                          {e.statut}
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-3">
                      <span>{e.prestataire}</span>
                      {e.numero_contrat && <span className="font-mono">#{e.numero_contrat}</span>}
                      {e.date_echeance && <span>→ {fmtDate(e.date_echeance)}</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-semibold text-slate-900">{fmtEur(e.montant_mensuel_ht)}</div>
                    <div className="text-xs text-slate-500">/mois HT</div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-slate-400 shrink-0" />
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
